import React, { useEffect, useMemo } from "react";
import { useScreenConfig } from "../hooks/useScreenConfig";
import { ComponentRenderer } from "./ComponentRenderer";
import { useAppDispatch, useAppSelector } from "../state/hooks";
import { useDarkMode } from "../contexts/DarkModeContext";
import { selectForms } from "../state/selectors";
import { updateScreenFormField } from "../state/slices/formsSlice";
import loaderLight from "../../public/images/loader_light.gif";
import loaderDark from "../../public/images/loader_dark.gif";
import { usePermissions } from "../hooks/usePermissions";
import type { ComponentConfig } from "../services/types/ComponentTypes";

interface ScreenRendererProps {
    screenId: string;
    onActionTrigger: (actionId: string, data?: any) => void;
    refreshKey?: number;
}

export const ScreenRenderer: React.FC<ScreenRendererProps> = ({
    screenId,
    onActionTrigger,
    refreshKey,
}) => {
    const { config, loading: configLoading, error, source } = useScreenConfig(screenId);
    const dispatch = useAppDispatch();
    const formsState = useAppSelector(selectForms);
    const { isDarkMode } = useDarkMode();
    const { hasPermission } = usePermissions();

    // Get form data and loading state from formsState
    // Note: 'loadingByScreen' in formsState tracks DATA loading (e.g. initial values), 
    // while 'configLoading' tracks CONFIGURATION loading.
    const isDataLoading = formsState.loadingByScreen[screenId] || false;
    const formData = formsState.formDataByScreen[screenId] || {};

    // Combine loading states
    const isLoading = configLoading || isDataLoading;

    /**
     * Recursively removes components from a config array that the user lacks
     * permission for. A component is removed when it has a `permissionKey` and
     * that permission evaluates to false for the given `pScreenId`.
     *
     * NOTE: must be defined before any early returns to satisfy Rules of Hooks.
     */
    const filterByPermission = useMemo(() => {
        return function filter(components: ComponentConfig[], pScreenId: string | undefined): ComponentConfig[] {
            return components.reduce<ComponentConfig[]>((acc, comp) => {
                const c = comp as any;

                // Check permission gate on the component itself
                if (c.permissionKey && pScreenId) {
                    if (!hasPermission(pScreenId, c.permissionKey)) {
                        return acc; // drop this component
                    }
                }

                // Determine child screenId: if this component declares its own screenId, use it;
                // otherwise inherit the parent's
                const childScreenId: string | undefined = c.screenId ?? pScreenId;

                // Filter SummeryActions (row-action buttons on SummaryList)
                let filteredSummeryActions = c.SummeryActions;
                if (Array.isArray(c.SummeryActions) && childScreenId) {
                    filteredSummeryActions = c.SummeryActions.filter(
                        (action: any) =>
                            !action.permissionKey ||
                            hasPermission(childScreenId, action.permissionKey)
                    );
                }

                // Filter fabAction
                let filteredFabAction = c.fabAction;
                if (c.fabAction && c.fabAction.permissionKey && childScreenId) {
                    if (!hasPermission(childScreenId, c.fabAction.permissionKey)) {
                        filteredFabAction = undefined;
                    }
                }

                // Recurse into childComponents
                if (c.childComponents && Array.isArray(c.childComponents)) {
                    const filtered = filter(c.childComponents, childScreenId);
                    const newC = { ...c, childComponents: filtered };
                    if (filteredSummeryActions !== c.SummeryActions) newC.SummeryActions = filteredSummeryActions;
                    if (filteredFabAction === undefined) delete newC.fabAction;
                    else if (filteredFabAction !== c.fabAction) newC.fabAction = filteredFabAction;
                    acc.push(newC);
                } else {
                    const newC = { ...c };
                    if (filteredSummeryActions !== c.SummeryActions) newC.SummeryActions = filteredSummeryActions;
                    if (filteredFabAction === undefined) delete newC.fabAction;
                    else if (filteredFabAction !== c.fabAction) newC.fabAction = filteredFabAction;

                    if (filteredSummeryActions !== c.SummeryActions || filteredFabAction !== c.fabAction) {
                        acc.push(newC);
                    } else {
                        acc.push(comp);
                    }
                }

                return acc;
            }, []);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPermission]);

    // Resolve the screenId declared in config (root ScreenGroupComponent may have screenId)
    // Use config ?? [] so this memo runs safely even before config is loaded.
    const configScreenId: string | undefined = (config?.[0] as any)?.screenId;

    // Apply permission-based filtering — MUST be before any early returns (Rules of Hooks)
    const filteredConfig = useMemo(
        () => filterByPermission(config ?? [], configScreenId),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [config, configScreenId, filterByPermission]
    );

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-[50vh] flex-col gap-4">
                <div className="w-10 h-10" />
                <img
                    src={isDarkMode ? loaderDark : loaderLight}
                    alt="Loading"
                    className="w-00 h-80"
                />
                {configLoading && <p className="text-gray-500 text-sm">Loading configuration...</p>}
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 text-center text-danger-600 dark:text-danger-400">
                {error}
            </div>
        );
    }

    if (!config) {
        return (
            <div className="p-6 text-center text-danger-600 dark:text-danger-400">
                Screen configuration not found for ID: {screenId}
            </div>
        );
    }

    // Dev indicator for config source
    const showDevIndicator = import.meta.env.DEV && source;


    return (
        <div className="relative">
            {showDevIndicator && (
                <div
                    className={`absolute top-0 right-0 z-50 px-2 py-1 text-xs font-bold rounded-bl-md shadow-sm pointer-events-none opacity-90 border-l border-b ${
                        source === 'API'
                            ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/90 dark:text-blue-200 dark:border-blue-700'
                            : 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-amber-900/90 dark:text-amber-200 dark:border-amber-700'
                    }`}
                    title={`Configuration loaded from ${source}`}
                >
                    Config: {source}
                </div>
            )}

            <ComponentRenderer
                config={filteredConfig}
                formData={formData}
                onFormDataChange={(name, value) =>
                    dispatch(updateScreenFormField({ screenId, name, value }))
                }
                onActionTrigger={onActionTrigger}
                refreshKey={refreshKey}
            />
        </div>
    );
};
